// components/OrdersCharts.jsx
import React, { useEffect, useRef, useState } from 'react';
import { MdOutlineAnalytics } from 'react-icons/md';
import Chart from 'chart.js/auto';
import { useAuth } from '../../context/UserContext';
import adminServices from '../../services/AdminServices';
import vendorServices from '../../services/VendorSevices';

function OrdersCharts() {
    const { user } = useAuth();
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('daily');
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!user) return;

        let services;
        if (user?.is_admin) {
            services = adminServices;
        } else if (user?.is_vendor) {
            services = vendorServices;
        }

        if (!services || typeof services.paymentCharts !== 'function') {
            setError('Metrics not available');
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await services.paymentCharts(`?period=${period}`);
                setChartData(res.data);
            } catch (err) {
                console.error('Chart error:', err);
                setError('Failed to load metrics');
                setChartData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, period]);

    useEffect(() => {
        // Guard: only render if we have valid data and canvas
        if (
            !chartRef.current ||
            !Array.isArray(chartData) ||
            chartData.length === 0 ||
            loading
        ) {
            return;
        }

        const ctx = chartRef.current.getContext('2d');
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Safely parse numbers
        const labels = chartData.map((item) => item.date);
        const revenueData = chartData.map((item) => parseFloat(item.total) || 0);
        const ordersData = chartData.map((item) => parseInt(item.count, 10) || 0);

        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Revenue (KES)',
                        data: revenueData,
                        borderColor: '#991b1b',
                        backgroundColor: 'rgba(153, 27, 27, 0.1)',
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Orders',
                        data: ordersData, 
                        borderColor: '#f97316',
                        backgroundColor: 'rgba(249, 115, 22, 0.1)',
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y1',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    x: { grid: { display: false }, ticks: { color: '#1e293b' } },
                    y: {
                        position: 'left',
                        title: { display: true, text: 'Revenue (KES)', color: '#1e293b' },
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: { color: '#1e293b' },
                        beginAtZero: true,
                    },
                    y1: {
                        position: 'right',
                        title: { display: true, text: 'Orders', color: '#1e293b' },
                        grid: { drawOnChartArea: false },
                        ticks: { color: '#1e293b' },
                        beginAtZero: true,
                    },
                },
                plugins: {
                    legend: { labels: { color: '#1e293b', usePointStyle: true } },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: '#1e293b',
                        titleColor: '#cbd5e1',
                        bodyColor: '#cbd5e1',
                        callbacks: {
                            label: function(context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    if (context.dataset.label === 'Revenue (KES)') {
                                        label += 'KES ' + context.parsed.y.toLocaleString('en-KE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        });
                                    } else {
                                        label += context.parsed.y;
                                    }
                                }
                                return label;
                            }
                        }
                    },
                },
            },
        });
    }, [chartData, loading]);

    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 flex gap-3 items-center">
                    <MdOutlineAnalytics className="h-7 w-7" />
                    Metrics
                </h3>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setPeriod('daily')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${period === 'daily'
                            ? 'bg-primary text-white'
                            : 'bg-base-200 text-neutral hover:bg-base-300 dark:bg-slate-700 dark:hover:bg-slate-600'
                            }`}
                    >
                        30 Days
                    </button>
                    <button
                        onClick={() => setPeriod('monthly')}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${period === 'monthly'
                            ? 'bg-primary text-white'
                            : 'bg-base-200 text-neutral hover:bg-base-300 dark:bg-slate-700 dark:hover:bg-slate-600'
                            }`}
                    >
                        12 Months
                    </button>
                </div>
            </div>

            <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden border border-border-light dark:border-border-dark">
                {loading ? (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                        Loading metrics...
                    </div>
                ) : error ? (
                    <div className="h-64 flex items-center justify-center text-error">
                        {error}
                    </div>
                ) : chartData && chartData.length > 0 ? (
                    <div className="h-72">
                        <canvas ref={chartRef} />
                    </div>
                ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                        No payment data available
                    </div>
                )}
            </div>
            
        </div>
    );
}

export default OrdersCharts;